import { mergeAttributes } from '@tiptap/core'
import Link from '@tiptap/extension-link'
import * as linkify from 'linkifyjs'
import { isBlockedExternalNavigation } from 'lib/externalNavigationPolicy'
import type { PmMark, PmNode } from 'lib/value'

export const BLOCKED_EXTERNAL_NAVIGATION_CLASS = 'treechat-blocked-scam-link'
export const BLOCKED_EXTERNAL_NAVIGATION_TOAST =
    'Treechat blocked this suspected scam link because it impersonates an official Treechat domain.'

type LinkRenderAttributes = Record<string, string | null>

export function getBlockedExternalNavigationRenderAttributes(
    url: string | null | undefined,
): LinkRenderAttributes {
    if (!isBlockedExternalNavigation(url)) return {}

    return {
        href: '#',
        target: null,
        rel: null,
        class: BLOCKED_EXTERNAL_NAVIGATION_CLASS,
        'data-blocked-scam-url': url as string,
        title: 'Blocked suspected scam link',
    }
}

export function getBlockedExternalNavigationUrl(element: Element): string | null {
    const blockedUrl = element.getAttribute('data-blocked-scam-url')
    if (isBlockedExternalNavigation(blockedUrl)) return blockedUrl

    const href = element.getAttribute('href')
    return isBlockedExternalNavigation(href) ? href : null
}

function addBlockedLinkMarks(node: PmNode): PmNode[] {
    if (node.type !== 'text' || !node.text) return [node]

    const marks = node.marks || []
    if (marks.some(mark => mark.type === 'link' || mark.type === 'code')) return [node]

    const blockedMatches = linkify
        .find(node.text)
        .filter(match => match.type === 'url' && isBlockedExternalNavigation(match.href))
    if (blockedMatches.length === 0) return [node]

    const content: PmNode[] = []
    let cursor = 0

    blockedMatches.forEach(match => {
        if (match.start > cursor) {
            content.push({ ...node, text: node.text!.slice(cursor, match.start) })
        }

        const linkMark: PmMark = { type: 'link', attrs: { href: match.href } }
        content.push({
            ...node,
            text: node.text!.slice(match.start, match.end),
            marks: [...marks, linkMark],
        })
        cursor = match.end
    })

    if (cursor < node.text.length) {
        content.push({ ...node, text: node.text.slice(cursor) })
    }

    return content
}

export function markBlockedExternalNavigationsInPmJson(document: PmNode): PmNode {
    if (!document.content) return document

    return {
        ...document,
        content: document.content.flatMap(node => {
            if (node.type === 'text') return addBlockedLinkMarks(node)
            return [markBlockedExternalNavigationsInPmJson(node)]
        }),
    }
}

export const SafeExternalLink = Link.extend({
    renderHTML({ HTMLAttributes }) {
        const href = typeof HTMLAttributes.href === 'string' ? HTMLAttributes.href : null
        return [
            'a',
            mergeAttributes(
                this.options.HTMLAttributes,
                HTMLAttributes,
                getBlockedExternalNavigationRenderAttributes(href),
            ),
            0,
        ]
    },
})
